import React, { useEffect, useState } from 'react'
import publicRoutes from './public'
import dashRoutes from './dash'
import hybridRoutes from './hybrid';
import protectedRoutes from './protected';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import AuthProvider, { useAuth } from "context/auth/Auth";
import paths from "routes/paths";
import Home from 'screens/home/Home';
import HomeWrapper from 'screens/home/HomeWrapper';
import UnifiedEventsSearchPage from 'modules/events/search/UnifiedEventsSearchPage';
import UnifiedEstablishmentsSearchPage from 'modules/establishment/search/UnifiedEstablishmentsSearchPage';
import PublicRestaurantMenu from 'screens/restaurant/PublicRestaurantMenu';
import OtpLogin from 'screens/restaurant/OtpLogin';
import WaiterApp from 'screens/restaurant/WaiterApp';
import StationApp from 'screens/restaurant/StationApp';
import usePageTitle from 'hooks/usePageTitle';

function RedirectCitySlug({ base }) {
    const { citySlug } = useParams();
    return <Navigate to={`${base}?city=${citySlug}`} replace />;
}

export default function AppRouter() {
    return (
        <AuthProvider>
            <AppRoutes />
        </AuthProvider>
    );
}

function AppRoutes() {
    const { isAuth, logout } = useAuth();

    // Intercepta login de parceiro SINCRONAMENTE, antes do primeiro render.
    // Se já há sessão ativa (isAuth=true), a rota /entrar (pública) não existe,
    // então a limpeza do token precisa acontecer ANTES do React decidir quais rotas montar.
    const [isRedirecting] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const loginPartner = params.get('loginWithPartner');

        if (loginPartner == 1) {
            // 1. Limpa a sessão anterior completamente
            localStorage.removeItem('token');
            localStorage.removeItem('user');

            // 2. Lê o novo token do cookie vindo do redirect do parceiro
            const cookies = document.cookie.split('; ');
            const tokenCookie = cookies.find((c) => c.startsWith('token='));
            const newToken = tokenCookie ? tokenCookie.split('=')[1] : null;

            if (newToken) {
                // 3. Seta o novo token
                localStorage.setItem('token', newToken);
            }

            // 4. Limpa o parâmetro da URL para evitar loop
            params.delete('loginWithPartner');
            const cleanSearch = params.toString();
            const cleanUrl = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`;
            window.history.replaceState({}, '', cleanUrl);

            // 5. Recarrega para reinicializar com o novo token
            window.location.reload();
            return true;
        }

        return false;
    });

    // Não renderiza nada enquanto está fazendo o reload do partner login
    if (isRedirecting) return null;

    return (
        <BrowserRouter>
            <PageTitleUpdater />
            <Routes>
                <Route path="/" element={<Home />}>
                    <Route index element={<Navigate to={paths.events.home} replace />} />
                    <Route path={paths?.events?.home} />
                    <Route path={paths?.establishments?.home} />
                </Route>

                {/* Rotas públicas */}
                {!isAuth ?
                    publicRoutes.map((route, i) => (
                        <Route
                            key={i}
                            exact={route.exact}
                            path={route.path}
                            element={route.element}
                        />
                    ))
                    : <Route path="*" element={<Navigate to="/" replace />} />
                }

                {/* Search results pages — must come BEFORE /:citySlug dynamic routes */}
                <Route path="/eventos/buscar" element={<HomeWrapper><UnifiedEventsSearchPage /></HomeWrapper>} />
                <Route path="/estabelecimentos/buscar" element={<HomeWrapper><UnifiedEstablishmentsSearchPage /></HomeWrapper>} />

                {/* Legacy city slug routes — redirect to unified search with city query param */}
                <Route path="/eventos/:citySlug" element={<RedirectCitySlug base="/eventos/buscar" />} />
                <Route path="/estabelecimentos/:citySlug" element={<RedirectCitySlug base="/estabelecimentos/buscar" />} />

                {/* Rota pública do menu do restaurante */}
                <Route path="/:slug/menu" element={<PublicRestaurantMenu />} />

                {/* Rotas do garçom / estação */}
                <Route path={paths.otp.login} element={<OtpLogin />} />
                <Route path={paths.restaurant.waiterApp} element={<WaiterApp />} />
                <Route path={paths.restaurant.stationApp} element={<StationApp />} />

                {/* Rotas do hibridas */}
                {
                    hybridRoutes.map((route, i) => (
                        <Route
                            key={i}
                            exact={route.exact}
                            path={route.path}
                            element={route.element}
                        />
                    ))
                }

                {/* Rotas protegidas */}
                {
                    isAuth ?
                        protectedRoutes.map((route, i) => (
                            <Route
                                key={i}
                                exact={route.exact}
                                path={route.path}
                                element={route.element}
                            />
                        ))
                        : <Route path="*" element={<Navigate to="/" replace />} />
                }

                {/* Rotas publicas */}
                {
                    !isAuth ? (
                        publicRoutes.map((route, i) => (
                            <Route key={i} {...route} element={<Route element={route.element} />} />
                        ))
                    ) : <Route path="*" element={<Navigate to="/" replace />} />
                }

                {/* Rotas do dashboard */}
                {isAuth
                    ? dashRoutes.map((route, i) => (
                        <Route key={i} {...route} element={<ProtectedRoute element={route.element} />} />
                    ))
                    : <Route path="*" element={<Navigate to="/" replace />} />
                }
            </Routes>

        </BrowserRouter>
    )
}

function ProtectedRoute({ element }) {
    const { isAuth } = useAuth();
    return isAuth ? element : <Navigate to="/" replace />;
}

function PageTitleUpdater() {
    usePageTitle();
    return null;
}
